import { create } from 'zustand';
import {
  fetchVendorPrices,
  createVendorPrice,
  updateVendorPrice,
  deleteVendorPrice,
} from '@/api/onderaannemerPrijzen';
import type { VendorPrices, VendorPriceRow, PriceTier } from '@/api/onderaannemerPrijzen';

type ToastKind = 'success' | 'error';

interface Toast {
  kind: ToastKind;
  message: string;
}

interface OnderaannemerPrijzenState {
  vendors: VendorPrices[];
  selectedVendorId: number | null;
  loading: boolean;
  error: string | null;
  toast: Toast | null;

  fetchPrices: () => Promise<void>;
  selectVendor: (id: number | null) => void;
  addPrice: (productId: number, tier: PriceTier, price: number) => Promise<void>;
  editPrice: (id: number, productId: number, tier: PriceTier, price: number) => Promise<void>;
  removePrice: (id: number, productId: number, tier: PriceTier) => Promise<void>;
  clearToast: () => void;
}

function isUnder(tier: PriceTier): boolean {
  return tier === 'under200';
}

/** Map a single tier price + id onto a copy of the matching row, leaving others untouched. */
function patchRow(
  rows: VendorPriceRow[],
  productId: number,
  tier: PriceTier,
  price: number | null,
  id: number | null,
): VendorPriceRow[] {
  return rows.map((row) => {
    if (row.productId !== productId) return row;
    return isUnder(tier)
      ? { ...row, priceUnder200: price, idUnder200: id }
      : { ...row, priceFrom200: price, idFrom200: id };
  });
}

function applyToVendor(
  vendors: VendorPrices[],
  vendorId: number,
  fn: (rows: VendorPriceRow[]) => VendorPriceRow[],
): VendorPrices[] {
  return vendors.map((v) => (v.vendorId === vendorId ? { ...v, rows: fn(v.rows) } : v));
}

export const useOnderaannemerPrijzenStore = create<OnderaannemerPrijzenState>((set, get) => ({
  vendors: [],
  selectedVendorId: null,
  loading: false,
  error: null,
  toast: null,

  fetchPrices: async () => {
    set({ loading: true, error: null });
    try {
      const data = await fetchVendorPrices();
      set({ vendors: data.vendors, loading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Prijzen laden mislukt';
      set({ error: message, loading: false });
    }
  },

  selectVendor: (id) => set({ selectedVendorId: id }),

  addPrice: async (productId, tier, price) => {
    const vendorId = get().selectedVendorId;
    if (vendorId == null) return;

    const prev = get().vendors;
    // Optimistic: show the price immediately (id unknown yet → temporary 0).
    set({
      vendors: applyToVendor(prev, vendorId, (rows) => patchRow(rows, productId, tier, price, 0)),
      toast: null,
    });

    try {
      const { id } = await createVendorPrice({ vendorId, productId, tier, price });
      set((state) => ({
        vendors: applyToVendor(state.vendors, vendorId, (rows) =>
          patchRow(rows, productId, tier, price, id),
        ),
        toast: { kind: 'success', message: 'Prijs toegevoegd' },
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Toevoegen mislukt';
      set({ vendors: prev, toast: { kind: 'error', message } });
    }
  },

  editPrice: async (id, productId, tier, price) => {
    const vendorId = get().selectedVendorId;
    if (vendorId == null) return;

    const prev = get().vendors;
    set({
      vendors: applyToVendor(prev, vendorId, (rows) => patchRow(rows, productId, tier, price, id)),
      toast: null,
    });

    try {
      await updateVendorPrice(id, price);
      set({ toast: { kind: 'success', message: 'Prijs bijgewerkt' } });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Bijwerken mislukt';
      set({ vendors: prev, toast: { kind: 'error', message } });
    }
  },

  removePrice: async (id, productId, tier) => {
    const vendorId = get().selectedVendorId;
    if (vendorId == null) return;

    const prev = get().vendors;
    set({
      vendors: applyToVendor(prev, vendorId, (rows) => patchRow(rows, productId, tier, null, null)),
      toast: null,
    });

    try {
      await deleteVendorPrice(id);
      set({ toast: { kind: 'success', message: 'Prijs verwijderd' } });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verwijderen mislukt';
      set({ vendors: prev, toast: { kind: 'error', message } });
    }
  },

  clearToast: () => set({ toast: null }),
}));
